/**
 * title / description 的解析规则（构建期与客户端共用）
 *
 * 为什么单独抽一个文件：预渲染时写进静态 HTML 的文案，和 SPA 内跳转后写进
 * document.title / meta description 的文案，必须由同一套规则算出来。以前这两处
 * 各写一份，改了构建期忘了客户端，站点内外就出现两套标题。
 *
 * 用 CommonJS 写：build/ 下的 Node 脚本直接 require，src 下的 webpack 也能打包，
 * 不用为两边各维护一份。
 */

const META = require('./pages-meta.json')
const DETAIL = require('./detail-meta.json')

// title 上限：超过 30 字百度会截断，品牌词就看不见了
const TITLE_MAX = 30
// description 上限：百度 PC 结果页只展示约 78 字
const DESC_MAX = 78

/** 品牌表：isxunjee 布尔值 → 该品牌的页面文案 */
function pagesOf(isxunjee) {
  return META.brands[isxunjee ? 'xunjee' : 'changyou'].pages
}

/** 品牌短名（标题末段用） */
function shortOf(isxunjee) {
  return META.brands[isxunjee ? 'xunjee' : 'changyou'].short
}

/** 从 /product/abc-def 这类路径取出基础路径 */
function basePath(route) {
  const seg = String(route).split('/').filter(Boolean)
  return seg.length ? '/' + seg[0] : '/'
}

/** 按字数截断，末尾补省略号（与文档 cut() 一致） */
function cut(str, n) {
  return str.length <= n ? str : str.slice(0, n - 1) + '…'
}

/**
 * 去掉「标准产品中心：」这类抬头，只留正文，便于在详情页前拼上详情名
 * 前 20 字内出现冒号就当作抬头——「基于企业微信与腾讯生态的定制应用：」这种
 * 17 字的标签式开头也算，否则详情页会拼出「任务管理：基于……定制应用：SCRM」
 * 这样连着两个冒号的句子。
 */
function stripLead(text) {
  const i = text.indexOf('：')
  return i > -1 && i <= 20 ? text.slice(i + 1) : text
}

/**
 * 详情页标题的中间段
 * 栏目 title 的第二段对详情页没有信息量时（如「案例详情」），换成有搜索价值的说法
 */
const DETAIL_MID = {
  '/crmcaseinfo': '客户案例',
  '/crmcase': '客户案例'
}

// 详情页中间段要跳过的通用栏目名（拿它拼进标题等于没拼）
const MID_STOPWORDS = ['案例详情', '产品中心', '应用中心', '解决方案', '首页', '关于我们', '联系我们']

/**
 * 详情页标题：详情名_栏目关键词_品牌
 * 超长时先丢中间段，仍超长则截详情名——品牌词必须留住
 */
function detailTitle(detailName, colTitle, short, base) {
  let mid = DETAIL_MID[base]
  if (!mid) {
    const parts = String(colTitle || '').split('_')
    // title 末段就是品牌词，取它之前的部分当栏目关键词
    const idx = parts.lastIndexOf(short)
    const mids = (idx > -1 ? parts.slice(0, idx) : parts).filter(
      (m) => m && MID_STOPWORDS.indexOf(m) < 0
    )
    mid = mids[0] || ''
  }

  const full = [detailName, mid, short].filter(Boolean).join('_')
  if (full.length <= TITLE_MAX) return full

  const brief = detailName + '_' + short
  if (brief.length <= TITLE_MAX) return brief

  return cut(detailName, TITLE_MAX - short.length - 1) + '_' + short
}

/**
 * 逐条定稿的详情页文案（detail-meta.json）
 *
 * 详情页原先靠「h1：栏目描述」拼接，35 个应用页因此拿到同一段描述尾巴
 * （「SCRM、会话内容存档、协同办公……」），文不对题。定稿表按页面逐条写，
 * 这里只负责查表。
 */
function lookupDetail(isxunjee, base, detailName) {
  if (!detailName) return null
  const table = DETAIL.brands[isxunjee ? 'xunjee' : 'changyou']
  const map = table && table[base]
  if (!map) return null
  if (map[detailName]) return map[detailName]
  // h1 常带栏目前缀（「客户案例：制造」），去掉抬头再试一次
  const stripped = stripLead(detailName)
  if (stripped !== detailName && map[stripped]) return map[stripped]
  return null
}

/**
 * 案例详情页：按「简称 + 行业 + 案例」由模板生成，不逐条硬编码
 *
 * 报告 8.4 的三条规则：
 *   一、标题用客户简称，不用公司全名（部分央企/金融机构全名不宜进 SEO 标题）
 *   二、描述的实施内容必须来自真实项目，不能套模板
 *   三、全名 -> 简称的对应关系见 detail-meta.json 的 caseShort
 *
 * seed 是构建期从 /CrmCase/GetDetails 取到的真实字段：
 *   name 客户全名 / industry 所属案例分类 / ptitle 方案名 / text 项目简介
 * 客户端拿不到 seed，只能退化成「简称 + 通用描述」，因此静态 HTML 里的文案
 * 才是准确的（爬虫读的是静态 HTML）。
 */
/** 客户全名 -> 简称。精确匹配失败时按最长键做包含替换（全名可能带前后缀） */
function shortCaseName(fullName) {
  if (!fullName) return ''
  const exact = DETAIL.caseShort[fullName]
  if (exact) return exact
  const keys = Object.keys(DETAIL.caseShort).sort(function (a, b) {
    return b.length - a.length
  })
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i]
    const idx = fullName.indexOf(k)
    if (idx > -1) return DETAIL.caseShort[k] + fullName.slice(idx + k.length)
  }
  return fullName
}

function caseMeta(seed, short, fallbackName) {
  const fullName = (seed && seed.name) || fallbackName || ''
  const name = shortCaseName(fullName)
  const industry = (seed && seed.industry) || ''
  let scene = (seed && seed.ptitle) || ''

  // 名字末尾与场景开头重复时去掉重复段：
  // 后台录入的 name 可能已含方案名（「…公众号移动办公」+「公众号移动办公平台」）
  if (scene && name.indexOf(scene) > -1) {
    scene = ''
  } else if (scene) {
    for (let n = Math.min(name.length, scene.length); n >= 4; n--) {
      if (name.slice(-n) === scene.slice(0, n)) {
        scene = scene.slice(n)
        break
      }
    }
  }

  // 品牌词必须留住：先算尾部，再按剩余长度截主名
  const suffix = [industry ? industry + '场景' : '', short].filter(Boolean).join('_')
  const head = name + (scene || '') + '案例'
  const title = cut(head, Math.max(TITLE_MAX - suffix.length - 1, 8)) + '_' + suffix

  const body = (seed && seed.text) || ''
  let desc
  if (body) {
    desc = name + (industry || '') + '案例：' + body
  } else {
    desc =
      name + (industry || '') + '案例：' +
      (scene || '项目实施') +
      '，含项目背景、实施路径与上线配置，武汉团队实施，同类企业可对照判断能否复用。'
  }
  return { title: title, description: cut(desc, DESC_MAX) }
}

/**
 * 解析某个路由的 title / description
 * @param {string} route        形如 /product/xxx
 * @param {boolean} isxunjee    是否讯捷品牌
 * @param {string} [detailName] 详情页的真实名称（接口数据或 h1）
 * @param {Object} [seed]       /crmcaseinfo 的真实项目字段（构建期才有）
 * @returns {{title: string, description: string}}
 */
function resolveMeta(route, isxunjee, detailName, seed) {
  const base = basePath(route)
  const page = pagesOf(isxunjee)[base] || {}
  const short = shortOf(isxunjee)
  const isDetail = String(route).split('/').filter(Boolean).length > 1

  // 案例详情页走模板函数：字段来自接口，逐条不同
  if (isDetail && base === '/crmcaseinfo') {
    if (seed) return caseMeta(seed, short, detailName)
    if (detailName) {
      const name = shortCaseName(detailName)
      const body = stripLead(page.description || '')
      return {
        title: cut(name + '客户案例_项目背景与上线成效_' + short, TITLE_MAX),
        description: cut(name + '客户案例：' + body, DESC_MAX)
      }
    }
  }

  // 逐条定稿优先（/app、/crmcase、/wbIndustry）
  const fixed = lookupDetail(isxunjee, base, detailName)
  if (isDetail && fixed) {
    return { title: fixed.title, description: fixed.description }
  }

  // 其余详情页：一条 URL 一段独立文案，不能复用栏目描述，
  // 否则三十个产品页 / 案例页会共用同一段，被判重复内容只留一条
  if (isDetail && detailName) {
    const body = stripLead(page.description || '')
    return {
      title: detailTitle(detailName, page.title, short, base),
      description: body ? cut(detailName + '：' + body, DESC_MAX) : cut(detailName, DESC_MAX)
    }
  }

  // 栏目页：直接用定稿文案
  return {
    title: page.title || short,
    description: page.description || ''
  }
}

module.exports = {
  META,
  DETAIL,
  TITLE_MAX,
  DESC_MAX,
  basePath,
  pagesOf,
  shortOf,
  cut,
  stripLead,
  detailTitle,
  lookupDetail,
  caseMeta,
  shortCaseName,
  resolveMeta
}
